/**
 * CollectionsPage — VEN-07: Cobranza
 * Dos tabs: Facturas (estado de cobranza + aging) y Pagos (lista con confirmar/anular).
 */
import { useState, useMemo } from 'react'
import { fmtDate, formatRut } from '@/lib/utils'
import { useFilters, FilterBar, type FilterConfig } from '@/components/filters'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { EmptyState } from '@/components/EmptyState'
import {
  Banknote, Plus, Loader2, CheckCircle2, XCircle, FileText, CreditCard, ClipboardList,
} from 'lucide-react'
import {
  useCollections, useArAging, useCustomerPayments,
  useConfirmPayment, useCancelPayment, useSaleDocument, useCustomers,
  type CollectionInvoice, type CustomerPayment,
} from '../hooks/useSales'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import {
  type SortState,
  sortStateToParam,
  loadSortFromStorage,
  saveSortToStorage,
} from '@/components/SortableHeader'
import { DataTable } from '@/components/ds/DataTable'
import { ordenControlado } from '@/components/ds/ordenControlado'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { PaymentModal } from './PaymentModal'
import { PaymentDetailModal } from './PaymentDetailModal'
import { DocumentDetailModal } from './DocumentDetailModal'
import { CollectionLogModal } from './CollectionLogModal'
import { KebabMenu } from '@/components/KebabMenu'

type CollectionStatus = 'pending' | 'partial' | 'paid' | 'overdue'
type ActiveTab = 'invoices' | 'payments'

const COL_STATUS_CONFIG: Record<CollectionStatus, { label: string; cls: string; dotCls: string }> = {
  pending: { label: 'Pendiente', cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
  partial: { label: 'Pago Parcial', cls: 'bg-primary-50 text-primary-hover',   dotCls: 'bg-primary/60' },
  paid:    { label: 'Pagado',    cls: 'bg-success-surface text-success-content', dotCls: 'bg-emerald-400' },
  overdue: { label: 'Vencido',   cls: 'bg-danger-surface text-danger-content',        dotCls: 'bg-danger-solid' },
}

const PAY_STATUS_CONFIG: Record<string, { label: string; cls: string; dotCls: string }> = {
  registered: { label: 'Registrado', cls: 'bg-warning-surface text-warning-content',    dotCls: 'bg-warning-solid' },
  confirmed:  { label: 'Confirmado', cls: 'bg-success-surface text-success-content', dotCls: 'bg-emerald-400' },
  cancelled:  { label: 'Anulado',    cls: 'bg-danger-surface text-danger-content',        dotCls: 'bg-danger-solid' },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = COL_STATUS_CONFIG[status as CollectionStatus] ?? PAY_STATUS_CONFIG[status] ?? COL_STATUS_CONFIG.pending
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

const ADJ_BADGE_CFG = {
  adjusted:                { label: 'Ajustado',               cls: 'bg-warning-surface text-warning-content',   dotCls: 'bg-warning-solid' },
  annulled:                { label: 'Anulado',                cls: 'bg-danger-surface text-danger-content',       dotCls: 'bg-danger-solid' },
  annulled_after_payment:  { label: 'Anulado (crédito)',      cls: 'bg-danger-surface text-danger-content',       dotCls: 'bg-danger-solid' },
} as const

function AdjustmentBadge({ invoice }: { invoice: CollectionInvoice }) {
  const s = invoice.adjustment_status
  if (!s || s === 'normal') return null
  const cfg = ADJ_BADGE_CFG[s]
  const adjs = invoice.adjustments ?? []
  const tooltipLines = adjs.map(a =>
    `${a.type} #${a.folio} — ${fmt(a.amount)}${a.reason ? ` (${a.reason})` : ''} [${a.issue_date}]`
  ).join('\n') || cfg.label
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${cfg.cls}`}
      title={`${cfg.label}\n\n${tooltipLines}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

function CededBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 cursor-help"
      title="Factura cedida en una operación de factoring. El pago se registra en el módulo de Factoring, no en Cobranza."
    >
      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
      Cedida
    </span>
  )
}

function fmt(value: string | undefined) {
  if (!value) return '—'
  return formatMoneyForTenant(value)
}

export function CollectionsPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('invoices')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [selectedInvoice, setSelectedInvoice] = useState<CollectionInvoice | null>(null)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)

  // Payments tab state
  const [payStatusFilter, setPayStatusFilter] = useState('')
  const [payPage, setPayPage] = useState(1)
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)

  // Document detail modal state
  const [detailDocId, setDetailDocId] = useState<string | null>(null)
  const [logInvoice, setLogInvoice] = useState<CollectionInvoice | null>(null)
  const { data: detailDoc } = useSaleDocument(detailDocId)
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const customers = customersData?.customers ?? []

  const handleRegisterPayment = (invoice?: CollectionInvoice) => {
    setSelectedInvoice(invoice ?? null)
    setPaymentModalOpen(true)
  }

  return (
    <div className="space-y-5">
      {/* Botón registrar pago */}
      <div className="flex justify-end">
        <button
          onClick={() => handleRegisterPayment()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
          <Plus size={15} />Registrar Pago
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-divider-strong">
        <button
          onClick={() => setActiveTab('invoices')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px
            ${activeTab === 'invoices'
              ? 'border-primary text-primary'
              : 'border-transparent text-content-muted hover:text-content'}`}>
          <FileText size={15} />
          Facturas
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px
            ${activeTab === 'payments'
              ? 'border-primary text-primary'
              : 'border-transparent text-content-muted hover:text-content'}`}>
          <CreditCard size={15} />
          Pagos
        </button>
      </div>

      {activeTab === 'invoices' ? (
        <InvoicesTab
          statusFilter={statusFilter}
          setStatusFilter={(s) => { setStatusFilter(s); setPage(1) }}
          page={page}
          setPage={setPage}
          onRegisterPayment={handleRegisterPayment}
          onViewDocument={(id) => setDetailDocId(id)}
          onViewLogs={(inv) => setLogInvoice(inv)}
        />
      ) : (
        <PaymentsTab
          statusFilter={payStatusFilter}
          setStatusFilter={(s) => { setPayStatusFilter(s); setPayPage(1) }}
          page={payPage}
          setPage={setPayPage}
          onViewPayment={(id) => setSelectedPaymentId(id)}
        />
      )}

      {/* Modal de registro de pago */}
      {paymentModalOpen && (
        <PaymentModal
          preselectedInvoice={selectedInvoice}
          onClose={() => { setPaymentModalOpen(false); setSelectedInvoice(null) }}
        />
      )}

      {/* Modal de detalle de pago */}
      {selectedPaymentId && (
        <PaymentDetailModal
          paymentId={selectedPaymentId}
          onClose={() => setSelectedPaymentId(null)}
        />
      )}

      {/* Modal de detalle de documento */}
      <DocumentDetailModal
        open={!!detailDocId}
        onClose={() => setDetailDocId(null)}
        doc={detailDoc || null}
        customers={customers}
      />

      {/* Modal de gestiones de cobranza */}
      {logInvoice && (
        <CollectionLogModal
          documentId={logInvoice.id}
          customerName={logInvoice.customer_name}
          folio={logInvoice.folio}
          outstanding={logInvoice.outstanding}
          onClose={() => setLogInvoice(null)}
        />
      )}
    </div>
  )
}

// ─── Invoices Tab ────────────────────────────────────────────────────────────

function InvoicesTab({
  statusFilter,
  setStatusFilter,
  page,
  setPage,
  onRegisterPayment,
  onViewDocument,
  onViewLogs,
}: {
  statusFilter: string
  setStatusFilter: (s: string) => void
  page: number
  setPage: (fn: (p: number) => number) => void
  onRegisterPayment: (inv?: CollectionInvoice) => void
  onViewDocument: (id: string) => void
  onViewLogs: (inv: CollectionInvoice) => void
}) {
  const locale = useTenantLocale()
  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const, grow: true,
      placeholder: 'Buscar por folio, cliente, RUT o monto (> 100000, < 50000)…' },
    { key: 'date', type: 'date-range' as const, label: 'Emisión', placement: 'quick' as const, presets: true },
  ], [])
  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(() => 1), persistKey: 'collections-invoices' })

  // Orden por defecto: fecha de creación del registro, más reciente primero.
  // Key '-v2' para que el nuevo default aplique aunque el usuario tenga guardado
  // el orden anterior (due_date) de antes del cambio.
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('collections-invoices-sort-v2', { field: 'created_at', direction: 'desc' }),
  )
  const handleSortChange = (next: SortState) => {
    setSort(next)
    saveSortToStorage('collections-invoices-sort-v2', next)
    setPage(() => 1)
  }

  // Filtro por tramo de antigüedad — se activa al hacer clic en una tarjeta.
  // Es mutuamente excluyente con el filtro por estado (el aging excluye pagadas).
  const [agingBucket, setAgingBucket] = useState<string | null>(null)
  const handleBucketClick = (key: string) => {
    setAgingBucket(prev => (prev === key ? null : key))
    setStatusFilter('')
    setPage(() => 1)
  }
  const handleStatusClick = (s: string) => {
    setStatusFilter(s)
    setAgingBucket(null)
    setPage(() => 1)
  }

  const { data, isLoading } = useCollections({
    collection_status: statusFilter || undefined,
    ...filters.params,
    ordering: sortStateToParam(sort),
    aging_bucket: agingBucket || undefined,
    page,
    page_size: 25,
  })
  const { data: aging, isLoading: agingLoading } = useArAging()

  return (
    <>
      {/* Resumen de Aging */}
      <AgingSummary aging={aging} isLoading={agingLoading} activeBucket={agingBucket} onBucketClick={handleBucketClick} />

      {/* Buscador inteligente (folio / cliente / monto) + rango de fecha */}
      <FilterBar config={filterConfig} filters={filters} />

      <div className="flex gap-2 flex-wrap">
        {(['', 'pending', 'partial', 'overdue', 'paid'] as const).map(s => (
          <button key={s} onClick={() => handleStatusClick(s)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${statusFilter === s
                ? 'bg-primary text-on-primary border-primary'
                : 'border-divider text-content-muted hover:bg-soft'}`}>
            {s ? (COL_STATUS_CONFIG[s as CollectionStatus]?.label ?? s) : 'Todos'}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : !data?.invoices.length ? (
          <EmptyState
            icon={Banknote}
            title="Sin facturas por cobrar"
            hint={statusFilter ? 'No hay facturas con este estado.' : 'Las facturas emitidas y aceptadas aparecerán aquí.'}
            size="md"
          />
        ) : (
          <DataTable<typeof data.invoices[number]>
            columns={[
              // 🔴 Las `key` son los nombres que el backend espera en `?ordering=`:
              // esta tabla la pagina el SERVIDOR, así que el orden viaja. Si no
              // coincidieran, el backend lo ignoraría y la flecha mentiría.
              { key: 'customer', header: 'Cliente', sortable: true,
                render: inv => (
                  <>
                    <div className="text-sm font-medium text-content truncate max-w-[180px]">
                      {inv.customer_name}
                    </div>
                    <div className="text-xs text-content-subtle">{formatRut(inv.customer_tax_id)}</div>
                  </>
                ) },
              { key: 'folio', header: 'Folio', sortable: true,
                render: inv => (inv.folio ? (
                  <button
                    onClick={() => onViewDocument(inv.id)}
                    className="text-primary hover:text-primary-hover hover:underline font-medium"
                    title="Ver detalle del documento"
                  >
                    {inv.folio}
                  </button>
                ) : '—') },
              { key: 'due_date', header: 'Vencimiento', sortable: true,
                render: inv => (inv.due_date ? (
                  <DueDateCell due={inv.due_date} status={inv.collection_status} />
                ) : <span className="text-content-muted">—</span>) },
              { key: 'collection_status', header: 'Estado', sortable: true,
                render: inv => (
                  <div className="flex flex-col gap-1 items-start">
                    <StatusBadge status={inv.collection_status} />
                    <AdjustmentBadge invoice={inv} />
                    {inv.is_ceded && <CededBadge />}
                  </div>
                ) },
              { key: 'total', header: 'Total', numeric: true, sortable: true,
                render: inv => <span className="text-content">{fmt(inv.total)}</span> },
              { key: 'paid_amount', header: 'Cobrado', numeric: true,
                render: inv => (
                  <span className="text-success-content-soft font-medium">{fmt(inv.paid_amount)}</span>
                ) },
              { key: 'outstanding', header: 'Pendiente', numeric: true,
                render: inv => <span className="font-semibold text-content">{fmt(inv.outstanding)}</span> },
              { key: 'acciones', header: 'Acción', width: 150,
                render: inv => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {inv.collection_status !== 'paid' && (
                      inv.is_ceded ? (
                        <span
                          title="Documento cedido (factoring) — registra el pago en el módulo de Factoring"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-content-subtle border border-divider rounded-lg cursor-help">
                          Cedida
                        </span>
                      ) : (
                        <button
                          onClick={() => onRegisterPayment(inv)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-primary border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
                          <Plus size={11} />Pago
                        </button>
                      )
                    )}
                    <KebabMenu items={[
                      { label: 'Gestiones', icon: <ClipboardList size={14} />, onClick: () => onViewLogs(inv) },
                    ]} />
                  </div>
                ) },
            ]}
            rows={data.invoices}
            rowKey={inv => inv.id}
            minWidth={1100}
            surface={false}
            onRowClick={inv => onViewDocument(inv.id)}
            {...ordenControlado(sort, handleSortChange)}
            caption="Facturas por cobrar y su estado de cobranza"
          />
        )}
      </div>

      {/* Paginación */}
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data.count}
          onPageChange={p => setPage(() => p)}
          locale={locale}
        />
      )}
    </>
  )
}

// ─── Payments Tab ────────────────────────────────────────────────────────────

function PaymentsTab({
  statusFilter,
  setStatusFilter,
  page,
  setPage,
  onViewPayment,
}: {
  statusFilter: string
  setStatusFilter: (s: string) => void
  page: number
  setPage: (fn: (p: number) => number) => void
  onViewPayment: (id: string) => void
}) {
  const locale = useTenantLocale()
  const [acctFilter, setAcctFilter] = useState('')
  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const, grow: true,
      placeholder: 'Buscar por cliente, RUT, referencia o monto (> 100000)…' },
    { key: 'date', type: 'date-range' as const, label: 'Fecha de pago', placement: 'quick' as const, presets: true },
  ], [])
  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(() => 1), persistKey: 'collections-payments' })
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('collections-payments-sort', { field: 'payment_date', direction: 'desc' }),
  )
  const handleSortChange = (next: SortState) => {
    setSort(next)
    saveSortToStorage('collections-payments-sort', next)
    setPage(() => 1)
  }
  const { data, isLoading } = useCustomerPayments({
    status: statusFilter || undefined,
    accounting_status: acctFilter || undefined,
    ...filters.params,
    ordering: sortStateToParam(sort),
    page,
    page_size: 25,
  })
  const confirmPayment = useConfirmPayment()
  const cancelPayment = useCancelPayment()
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const handleConfirm = (payment: CustomerPayment) => {
    confirmAction({
      message: `¿Confirmar el pago de ${fmt(payment.amount)} de ${payment.customer_name}?`,
      variant: 'success',
      onConfirm: () => confirmPayment.mutate(payment.id),
    })
  }

  const handleCancel = (payment: CustomerPayment) => {
    confirmAction({
      message: `¿Anular el pago de ${fmt(payment.amount)} de ${payment.customer_name}?`,
      variant: 'danger',
      onConfirm: () => cancelPayment.mutate(payment.id),
    })
  }

  return (
    <>
      {/* Buscador inteligente (cliente / referencia / monto) + rango de fecha */}
      <FilterBar config={filterConfig} filters={filters} />

      <div className="flex gap-2 flex-wrap">
        {(['', 'registered', 'confirmed', 'cancelled'] as const).map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${statusFilter === s
                ? 'bg-primary text-on-primary border-primary'
                : 'border-divider text-content-muted hover:bg-soft'}`}>
            {s ? (PAY_STATUS_CONFIG[s]?.label ?? s) : 'Todos'}
          </button>
        ))}
        <span className="text-content-subtle mx-1">|</span>
        {[
          { value: '', label: 'Todos' },
          { value: 'pending', label: 'Sin contabilizar' },
          { value: 'accounted', label: 'Contabilizados' },
        ].map(opt => (
          <button key={opt.value} onClick={() => { setAcctFilter(opt.value); setPage(() => 1) }}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${acctFilter === opt.value
                ? 'bg-warning-fill text-white border-warning-fill'
                : 'border-divider text-content-muted hover:bg-soft'}`}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : !data?.payments.length ? (
          <EmptyState
            icon={CreditCard}
            title="Sin pagos registrados"
            hint={statusFilter ? 'No hay pagos con este estado.' : 'Los pagos registrados aparecerán aquí.'}
            size="md"
          />
        ) : (
          <DataTable<typeof data.payments[number]>
            columns={[
              { key: 'customer', header: 'Cliente', sortable: true,
                render: pay => (
                  <div className="text-sm font-medium text-content truncate max-w-[180px]">
                    {pay.customer_name}
                  </div>
                ) },
              { key: 'payment_date', header: 'Fecha', sortable: true,
                render: pay => <span className="text-content-muted">{fmtDate(pay.payment_date, locale)}</span> },
              { key: 'payment_method', header: 'Método', sortable: true,
                render: pay => (
                  <span className="text-content-muted">{pay.payment_method_display ?? pay.payment_method}</span>
                ) },
              { key: 'reference', header: 'Referencia', sortable: true,
                render: pay => <span className="text-content-muted">{pay.reference || '—'}</span> },
              { key: 'status', header: 'Estado', sortable: true,
                render: pay => (
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={pay.status} />
                    {pay.status === 'confirmed' && pay.accounting_status === 'pending' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning-solid" />
                        Sin contabilizar
                      </span>
                    )}
                  </div>
                ) },
              { key: 'amount', header: 'Monto', numeric: true, sortable: true,
                render: pay => <span className="font-semibold text-content">{fmt(pay.amount)}</span> },
              { key: 'allocations', header: 'Asignaciones', numeric: true,
                render: pay => (
                  <span className="text-content-muted">
                    {pay.allocations?.length
                      ? `${pay.allocations.length} factura${pay.allocations.length > 1 ? 's' : ''}`
                      : <span className="text-warning-solid text-xs">Sin asignar</span>}
                  </span>
                ) },
              { key: 'acciones', header: 'Acciones', width: 170,
                render: pay => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {pay.status === 'registered' && (
                      <button
                        onClick={() => handleConfirm(pay)}
                        disabled={confirmPayment.isPending}
                        title="Confirmar pago"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-success-content-soft border border-success-border rounded-lg hover:bg-success-surface transition-colors disabled:opacity-50">
                        <CheckCircle2 size={12} />Confirmar
                      </button>
                    )}
                    <KebabMenu items={[
                      { label: 'Anular', icon: <XCircle size={14} />, onClick: () => handleCancel(pay), hidden: pay.status === 'cancelled', className: 'text-danger-content-soft hover:bg-danger-surface', disabled: cancelPayment.isPending },
                    ]} />
                  </div>
                ) },
            ]}
            rows={data.payments}
            rowKey={pay => pay.id}
            minWidth={1160}
            surface={false}
            onRowClick={pay => onViewPayment(pay.id)}
            {...ordenControlado(sort, handleSortChange)}
            caption="Pagos recibidos de clientes"
          />
        )}
      </div>

      {/* Paginación */}
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data.count}
          onPageChange={p => setPage(() => p)}
          locale={locale}
        />
      )}

      <ConfirmModal {...modalProps} />
    </>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DueDateCell({ due, status }: { due: string; status: CollectionStatus }) {
  const locale = useTenantLocale()
  const today = new Date()
  const dueDate = new Date(due)
  const days = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))
  const isOverdue = days > 0 && status !== 'paid'

  return (
    <span className={isOverdue ? 'text-danger-content-soft font-medium' : ''}>
      {fmtDate(due, locale)}
      {isOverdue && <span className="ml-1 text-xs">({days}d)</span>}
    </span>
  )
}

// ─── Resumen de Aging ─────────────────────────────────────────────────────────

import type { ArAgingResponse } from '../hooks/useSales'
import { Pagination } from '@/components/ds/Pagination'
import { AGING_BUCKETS } from '@/components/ds/agingBuckets'

function AgingSummary({
  aging,
  isLoading,
  activeBucket,
  onBucketClick,
}: {
  aging?: ArAgingResponse
  isLoading: boolean
  activeBucket?: string | null
  onBucketClick?: (key: string) => void
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="h-16 bg-surface-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  // La escala sale del catálogo único. La opacidad mayor en oscuro que acá se
  // resolvía a mano (a /30 amarillo, naranja y rojo se confunden) vive ahora en
  // los tokens `aging-*` de index.css.
  const buckets = AGING_BUCKETS.map(b => ({ key: b.key, label: b.label, color: b.card }))

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {buckets.map(({ key, label, color }) => {
        const bucket = aging?.buckets?.[key]
        const isActive = activeBucket === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onBucketClick?.(key)}
            aria-pressed={isActive}
            title={isActive ? 'Quitar filtro' : `Filtrar facturas: ${label}`}
            className={`text-left rounded-xl border p-3 transition-all cursor-pointer hover:brightness-105 hover:shadow-sm focus:outline-none ${color} ${
              isActive ? 'ring-2 ring-offset-1 ring-offset-canvas ring-current shadow-sm' : 'opacity-95'
            }`}
          >
            <p className="text-xs font-medium opacity-80">{label}</p>
            <p className="text-lg font-bold mt-0.5">
              {bucket?.count ?? 0}
            </p>
            <p className="text-xs opacity-70 truncate">
              {bucket?.total ? fmt(bucket.total) : '—'}
            </p>
          </button>
        )
      })}
    </div>
  )
}
